<script setup>
/**
 * Admin → Member Highlights (Kapil, 2026-09-23): every highlight a member
 * shared with a restaurant, in one list, newest first or A-Z by restaurant.
 *
 * Each row is the words the restaurant got, whose they are, whether their
 * Network sees them too, and the next step — Passed along (RM's record that
 * the relay email went out; the restaurant's account has it from the moment
 * it was shared), then Ask member (an email they answer
 * by replying), then "Post publicly on {Restaurant}'s page", which RM turns on
 * once they reply yes (Kapil, 2026-09-24). The rules are in utils/memberHighlightsAdmin (tested);
 * this page draws them. It replaces the per-member buttons on the Fans page.
 */
import { onMounted, ref, watch } from 'vue'
import axios from '@axios'
import { relativeAgo } from '@/utils/relativeAgo'
import { networkLine, passedAlong, privateLine, publicControl } from '@/utils/memberHighlightsAdmin'

const SORT_KEY = 'rm.memberHighlights.sort'

const readSort = () => {
  try { return localStorage.getItem(SORT_KEY) === 'az' ? 'az' : 'recent' } catch { return 'recent' }
}

const sort = ref(readSort())
const rows = ref([])
const loading = ref(false)
const error = ref('')
const busy = ref({}) // shareId → true while that row saves
const rowError = ref({})

const formatDate = d => new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })

async function load() {
  loading.value = true
  error.value = ''
  try {
    const { data } = await axios.get('/profile/admin/highlights', { params: { sort: sort.value } })

    rows.value = Array.isArray(data) ? data : []
  } catch (e) {
    error.value = e?.response?.data?.message || 'Could not load member highlights.'
  } finally {
    loading.value = false
  }
}

watch(sort, value => {
  try { localStorage.setItem(SORT_KEY, value) } catch { /* private mode */ }
  load()
})

onMounted(load)

// One save at a time per row; the row shows its own result or error.
async function act(row, request) {
  busy.value = { ...busy.value, [row.shareId]: true }
  rowError.value = { ...rowError.value, [row.shareId]: '' }
  try {
    await request()
    await load()
  } catch (e) {
    rowError.value = { ...rowError.value, [row.shareId]: e?.response?.data?.message || 'Could not save that. Please try again.' }
  } finally {
    busy.value = { ...busy.value, [row.shareId]: false }
  }
}

const setPassedAlong = (row, on) => act(row, () => axios.put(`/profile/admin/shares/${row.shareId}/delivery`, { state: on ? 'relayed' : 'alerted' }))

function askMember(row) {
  const who = row.member?.name || 'the member'
  // It emails a member, so it is confirmed.
  // eslint-disable-next-line no-alert
  if (!window.confirm(`Email ${who} to ask if ${row.restaurant?.name || 'the restaurant'} can show this on their RM page?`)) return
  act(row, () => axios.post(`/profile/admin/shares/${row.shareId}/request-public`))
}

const setOnPage = (row, show) => act(row, () => axios.put(`/profile/admin/shares/${row.shareId}/public`, { show }))
</script>

<template>
  <div class="member-highlights">
    <header class="member-highlights__header">
      <h1 class="member-highlights__title">
        Member Highlights
      </h1>
      <VBtnToggle
        v-model="sort"
        mandatory
        density="compact"
        variant="outlined"
        divided
        class="member-highlights__sort"
      >
        <VBtn value="recent">
          Recent
        </VBtn>
        <VBtn value="az">
          A-Z
        </VBtn>
      </VBtnToggle>
    </header>

    <p
      v-if="error"
      class="member-highlights__error"
    >
      {{ error }}
    </p>

    <div
      v-if="loading && !rows.length"
      class="member-highlights__skeleton"
    >
      <div class="member-highlights__skeleton-line" />
      <div class="member-highlights__skeleton-line short" />
      <div class="member-highlights__skeleton-line" />
    </div>

    <p
      v-else-if="!rows.length && !error"
      class="member-highlights__empty"
    >
      No highlights yet. When a member shares one with a restaurant, it shows here.
    </p>

    <ul
      v-else
      class="member-highlights__list"
    >
      <li
        v-for="row in rows"
        :key="row.shareId"
        class="member-highlights__row"
        :class="{ 'member-highlights__row--private': row.status === 'withdrawn' }"
      >
        <RouterLink
          v-if="row.restaurant?.profileId"
          :to="`/r/${row.restaurant.profileId}`"
          class="member-highlights__restaurant"
        >
          {{ row.restaurant.name }}
        </RouterLink>
        <span
          v-else
          class="member-highlights__restaurant"
        >{{ row.restaurant?.name || 'Restaurant' }}</span>

        <p class="member-highlights__words">
          "{{ row.text }}"
        </p>

        <p class="member-highlights__meta">
          <RouterLink
            :to="`/admin/fans/${row.member.id}`"
            class="member-highlights__member"
          >
            {{ row.member.name || 'Member' }}
          </RouterLink>
          <span>· {{ relativeAgo(row.sharedAt) }} ago</span>
        </p>
        <!-- Its own line, so a phone never starts a line with a separator. -->
        <p class="member-highlights__network">
          {{ networkLine(row) }}
        </p>

        <p
          v-if="privateLine(row)"
          class="member-highlights__private"
        >
          {{ privateLine(row) }}
        </p>

        <div
          v-else
          class="member-highlights__controls"
        >
          <VSwitch
            :model-value="passedAlong(row)"
            label="Passed along"
            color="primary"
            density="compact"
            hide-details
            inset
            :loading="busy[row.shareId]"
            :disabled="busy[row.shareId]"
            @update:model-value="setPassedAlong(row, $event)"
          />

          <template v-if="publicControl(row, { formatDate }).kind === 'ask'">
            <VBtn
              variant="text"
              color="primary"
              size="small"
              :loading="busy[row.shareId]"
              @click="askMember(row)"
            >
              Ask member
            </VBtn>
          </template>
          <template v-else-if="publicControl(row, { formatDate }).kind === 'toggle'">
            <VSwitch
              :model-value="row.onRestaurantPage"
              :label="publicControl(row, { formatDate }).label"
              color="primary"
              density="compact"
              hide-details
              inset
              :disabled="busy[row.shareId]"
              @update:model-value="setOnPage(row, $event)"
            />
            <span
              v-if="publicControl(row, { formatDate }).note && !row.onRestaurantPage"
              class="member-highlights__state"
            >
              {{ publicControl(row, { formatDate }).note }}
            </span>
          </template>
          <span
            v-else-if="publicControl(row, { formatDate }).label"
            class="member-highlights__state"
          >
            {{ publicControl(row, { formatDate }).label }}
          </span>
        </div>

        <p
          v-if="rowError[row.shareId]"
          class="member-highlights__error"
        >
          {{ rowError[row.shareId] }}
        </p>
      </li>
    </ul>
  </div>
</template>

<style scoped>
.member-highlights {
  max-width: 720px;
  margin: 0 auto;
  padding: 16px;
}

.member-highlights__header {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 16px;
}

.member-highlights__title {
  margin: 0;
  font-size: 24px;
  font-weight: 600;
  color: var(--ds-text-strong, rgb(var(--v-theme-on-surface)));
}

.member-highlights__list {
  margin: 0;
  padding: 0;
  list-style: none;
}

/* One highlight: the place, the words, whose, then the next step. */
.member-highlights__row {
  padding: 16px 0;
  border-top: 1px solid var(--ds-border-subtle, rgba(var(--v-theme-on-surface), 0.08));
}

.member-highlights__row--private .member-highlights__words {
  color: var(--ds-text-secondary, rgba(var(--v-theme-on-surface), 0.6));
}

.member-highlights__restaurant {
  font-size: 16px;
  font-weight: 600;
  color: var(--ds-text-strong, rgb(var(--v-theme-on-surface)));
  text-decoration: none;
}

.member-highlights__words {
  margin: 4px 0 0;
  font-size: 16px;
  line-height: 1.5;
  white-space: pre-line;
  color: var(--ds-text-body, rgb(var(--v-theme-on-surface)));
}

.member-highlights__meta {
  display: flex;
  flex-wrap: wrap;
  gap: 0 4px;
  margin: 4px 0 0;
  font-size: 14px;
  color: var(--ds-text-secondary, rgba(var(--v-theme-on-surface), 0.6));
}

.member-highlights__network {
  margin: 2px 0 0;
  font-size: 14px;
  color: var(--ds-text-secondary, rgba(var(--v-theme-on-surface), 0.6));
}

/* A switch's label is its state, so it reads at full strength. */
.member-highlights__controls :deep(.v-label) {
  opacity: 1;
  font-size: 14px;
  color: var(--ds-text-body, rgb(var(--v-theme-on-surface)));
}

.member-highlights__member {
  color: var(--ds-brand-primary-dark, rgb(var(--v-theme-primary)));
  text-decoration: none;
}

.member-highlights__controls {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 4px 24px;
  margin-top: 8px;
}

.member-highlights__state {
  font-size: 14px;
  color: var(--ds-text-secondary, rgba(var(--v-theme-on-surface), 0.6));
}

.member-highlights__private {
  margin: 8px 0 0;
  font-size: 14px;
  font-weight: 600;
  color: rgb(var(--v-theme-error));
}

.member-highlights__empty {
  font-size: 16px;
  color: var(--ds-text-secondary, rgba(var(--v-theme-on-surface), 0.6));
}

.member-highlights__error {
  margin: 8px 0 0;
  font-size: 14px;
  color: rgb(var(--v-theme-error));
}

.member-highlights__skeleton-line {
  height: 14px;
  margin-bottom: 12px;
  border-radius: 8px;
  background: rgba(var(--v-theme-on-surface), 0.08);
}

.member-highlights__skeleton-line.short {
  width: 60%;
}
</style>

<route lang="yaml">
meta:
  layout: website
  action: read
  subject: AclDemo
  requiresAdmin: true
</route>
